import type { ProductSearchOptions } from './productSearchOptions'
import type { FacetLabels } from '@/contexts/products/facets'
import type { FilterGroup } from '@/contexts/products/ProductsContext'
import type { FacetFilters, FacetsResponse } from '@/types/catalog'
import type { GetFacetsScope } from '@/types/catalog/api'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getFacets } from '@/api'
import { useCzListSetsContext } from '@/contexts/cz-list-sets/useCzListSetsContext'
import { mapFacetsToFilterGroups, resolveFacetGroupLabels, resolveFacetLabels } from '@/contexts/products/facets'
import { queryKeys } from '@/query/queryKeys'

interface ProductFacetsData {
  response: Partial<FacetsResponse>
  filterGroups: FilterGroup[]
  labels: FacetLabels
  loadedGroups: string[]
}

interface ProductFacetsOptions extends ProductSearchOptions {
  departmentId: number | undefined
  filters: FacetFilters
}

export function useProductFacets({
  season,
  departmentId,
  familiesIds,
  subdepartmentIds,
  viewMode,
  filters,
  currentLanguage = 'en',
  isNew = false,
  productCode,
}: ProductFacetsOptions) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { czListSetsByCustomZone } = useCzListSetsContext()
  const czListSetsKey = Object.entries(czListSetsByCustomZone)
    .map(([id, { designation }]) => [id, designation] as const)
    .sort(([firstId], [secondId]) => firstId.localeCompare(secondId))

  const queryKey = useMemo(
    () => queryKeys.productFacets({
      season,
      departmentId,
      familiesIds,
      subdepartmentIds,
      viewMode,
      productCode,
      filters,
      isNew,
      currentLanguage,
      czListSetsKey,
    }),
    [productCode, currentLanguage, czListSetsKey, departmentId, familiesIds, subdepartmentIds, filters, isNew, season, viewMode],
  )

  const queryKeyString = JSON.stringify(queryKey)
  let facetScope: GetFacetsScope | undefined

  if (departmentId !== undefined) {
    facetScope = { department: departmentId }
  }
  else if (familiesIds.length > 0) {
    facetScope = { families: familiesIds }
  }
  else if (subdepartmentIds?.length) {
    facetScope = { subdepartments: subdepartmentIds }
  }

  const query = useQuery<ProductFacetsData>({
    enabled: facetScope !== undefined,
    queryKey,
    queryFn: async ({ signal }) => {
      if (signal?.aborted)
        throw new DOMException('Facet request aborted', 'AbortError')
      if (!facetScope)
        throw new Error('Facet query requires a department or family scope')

      const response = await getFacets({
        season,
        ...facetScope,
        ...(subdepartmentIds?.length && { subdepartments: subdepartmentIds }),
        viewMode,
        productCode,
        filters,
        isNew,
      })

      const labels = await resolveFacetLabels(response, currentLanguage, t, czListSetsByCustomZone)
      return {
        response,
        filterGroups: mapFacetsToFilterGroups(t, response, labels),
        labels,
        loadedGroups: [],
      }
    },
    placeholderData: previousData => previousData,
  })

  const [loadingState, setLoadingState] = useState<{ key: string, groups: ReadonlySet<string> }>(() => ({
    key: queryKeyString,
    groups: new Set(),
  }))

  const loadingFacetGroups = loadingState.key === queryKeyString ? loadingState.groups : new Set<string>()

  const loadFacetGroupLabels = async (groupId: string) => {
    const currentData = query.data

    if (!currentData || currentData.loadedGroups.includes(groupId) || loadingFacetGroups.has(groupId))
      return

    setLoadingState(previous => ({
      key: queryKeyString,
      groups: new Set(previous.key === queryKeyString ? previous.groups : []).add(groupId),
    }))

    try {
      const labels = await resolveFacetGroupLabels(
        currentData.response,
        groupId,
        currentLanguage,
        t,
        czListSetsByCustomZone,
      )

      queryClient.setQueryData<ProductFacetsData>(queryKey, (previousData) => {
        if (!previousData || previousData.loadedGroups.includes(groupId))
          return previousData

        const mergedLabels = mergeFacetLabels(previousData.labels, labels)
        return {
          ...previousData,
          labels: mergedLabels,
          filterGroups: mapFacetsToFilterGroups(t, previousData.response, mergedLabels),
          loadedGroups: [...previousData.loadedGroups, groupId],
        }
      })
    }
    finally {
      setLoadingState((previous) => {
        const next = new Set(previous.key === queryKeyString ? previous.groups : [])
        next.delete(groupId)
        return { key: queryKeyString, groups: next }
      })
    }
  }

  return {
    ...query,
    data: query.data?.filterGroups,
    facetQueryKey: queryKeyString,
    loadFacetGroupLabels,
    loadingFacetGroups,
  }
}

function mergeFacetLabels(base: FacetLabels, additions: FacetLabels): FacetLabels {
  const merged: FacetLabels = { ...base }

  for (const [groupId, groupLabels] of Object.entries(additions)) {
    merged[groupId] = {
      ...base[groupId],
      ...groupLabels,
    }
  }

  return merged
}
